import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowUpRight, Clock } from "lucide-react";
import Seo from "@/components/Seo";
import XLogo from "@/components/XLogo";
import { Reveal } from "@/components/Reveal";
import { API } from "@/data/content";

export default function InsightsPage() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.__lenis?.scrollTo(0, { immediate: true });
    axios
      .get(`${API}/articles`)
      .then(({ data }) => setArticles(data.articles || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="insights-page" className="min-h-screen bg-void">
      <Seo
        title="Market Insights & Research — TEAM X"
        description="Educational research from the TEAM X desk: semiconductor cycles, risk management, mega-cap tech analysis, and market mechanics — written for learners."
      />
      <header className="glass sticky top-0 z-50 border-b border-line">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-4 sm:px-6">
          <Link to="/" data-testid="insights-back-link" className="flex items-center gap-2.5">
            <XLogo size={28} />
            <span className="font-display text-xl font-black uppercase tracking-[0.18em] text-ink">
              Team<span className="text-brand">&nbsp;X</span>
            </span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-mute transition-colors hover:text-brand"
          >
            <ArrowLeft size={13} /> Home
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-16 sm:px-6">
        <Reveal>
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-brand">
            Research Library
          </p>
          <h1 className="mt-3 font-display text-3xl font-black uppercase tracking-tight text-ink sm:text-5xl">
            Market <span className="text-brand">Insights</span>
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-mute sm:text-base">
            Educational research from the TEAM X desk — industry deep-dives,
            risk frameworks, and market mechanics. Informational content only;
            not financial advice.
          </p>
        </Reveal>

        <div className="mt-12 space-y-4">
          {loading && (
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-dim">
              Loading research…
            </p>
          )}
          {!loading && !articles.length && (
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-dim">
              No articles published yet.
            </p>
          )}
          {articles.map((a, i) => (
            <Reveal key={a.slug} delay={i * 0.06}>
              <Link
                data-testid={`insights-list-item-${a.slug}`}
                to={`/insights/${a.slug}`}
                className="card-trace group flex flex-col gap-5 rounded-xl border border-line bg-panel p-5 sm:flex-row sm:p-6"
              >
                <img
                  src={a.image}
                  alt={a.title}
                  loading="lazy"
                  className="clip-frame h-40 w-full shrink-0 rounded-lg object-cover sm:h-32 sm:w-52"
                />
                <div className="flex flex-1 flex-col">
                  <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.18em]">
                    <span className="text-brand">{a.category}</span>
                    <span className="text-dim">
                      {new Date(a.published_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    </span>
                    <span className="inline-flex items-center gap-1 text-dim">
                      <Clock size={10} /> {a.read_minutes} min
                    </span>
                  </div>
                  <h2 className="mt-2 flex items-start justify-between gap-3 font-display text-2xl font-bold uppercase leading-tight tracking-tight text-ink">
                    {a.title}
                    <ArrowUpRight
                      size={18}
                      className="mt-1 shrink-0 text-dim transition-colors group-hover:text-brand"
                    />
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed text-mute">{a.excerpt}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {(a.tags || []).map((t) => (
                      <span
                        key={t}
                        className="rounded-full border border-line px-2.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.12em] text-dim"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </main>
    </div>
  );
}
